// src/pages/CI/CommercialChat.js
//
// Agent console for the COMMERCIAL (C&I) support chat.
//
// Backend: /api/commercial-chat (sowash-backend/routes/commercialChatRoutes.js).
// Access is ci_admin ONLY — enforced server-side by requireSupportAgent, not by
// hiding the menu item. The residential console at /operations/customer-chat is
// the mirror image (Operations + Admin, and it excludes ci_admin on purpose).
//
// Model: ONE thread per CLIENT, with per-message visit tagging. The customer is
// a company, so every site manager on the account shares the conversation and
// each message records who sent it.
//
// ⚠ Unlike the residential console, staff HERE CAN START a conversation. The
// residential inbox hides threads with no messages so 1,463 empty customer rows
// do not flood it, which also made proactive outreach impossible. There are 19
// commercial clients with a login, so a picker over that list is trivial.
//
// Transport is polling — the backend has no websocket layer. Two cadences: the
// inbox every 15s, the open conversation every 8s (delta only, via after_id),
// and both pause when the browser tab is hidden.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { getAuthHeader } from '../../helpers/auth';
import { fmtPKTDateTime, fmtPKTTime } from '../../helpers/time';
// The rich preview a tagged visit renders as, and the popup it opens. Same
// payload the customer's app renders from — see ChatJobPreview.js.
import { VisitCard, JobDetailModal } from './ChatJobPreview';

const API = process.env.REACT_APP_API_BASE_URL;
// Attachments come back as "/uploads/..." — SERVER_BASE must not repeat /api.
const IMG_BASE = API ? API.replace('/api', '') : '';

// Teal rather than the residential console's blue. Two consoles that look
// identical are two consoles an agent will eventually mix up.
const T = {
  bg: 'var(--nd-bg)',
  surface: 'var(--nd-surface)',
  border: 'var(--nd-w06)',
  text: 'var(--nd-text)',
  textStrong: 'var(--nd-text-strong)',
  textMuted: 'var(--nd-tx35)',
  teal: '#0F766E',
  tealLight: '#2DD4BF',
  green: '#22D3A5',
  amber: '#F59E0B',
  red: '#F87171',
};

const GLOBAL = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600;9..40,700;9..40,800&family=DM+Mono:wght@400;500&display=swap');
  .cv-chat * { box-sizing: border-box; }
  .cv-chat ::-webkit-scrollbar { width: 7px; height: 7px; }
  .cv-chat ::-webkit-scrollbar-thumb { background: var(--nd-w08); border-radius: 4px; }
  .cv-chat ::-webkit-scrollbar-track { background: transparent; }

  .cv-chat input, .cv-chat select, .cv-chat textarea {
    background: var(--nd-w05); border: 1px solid var(--nd-w10); border-radius: 8px;
    color: var(--nd-text); font-family: 'DM Sans', sans-serif; font-size: 13px;
    padding: 8px 12px; outline: none;
  }
  .cv-chat input:focus, .cv-chat select:focus, .cv-chat textarea:focus { border-color: rgba(45,212,191,0.5); }

  .cv-chat .cv-thread { cursor: pointer; transition: background .12s; }
  .cv-chat .cv-thread:hover { background: rgba(45,212,191,0.06); }
  .cv-chat .cv-thread.cv-on { background: rgba(45,212,191,0.12); }

  .cv-chat .cv-tab {
    padding: 8px 14px; background: none; border: none; border-radius: 999px;
    color: var(--nd-tx35); font-family: 'DM Sans', sans-serif; font-weight: 700;
    font-size: 11.5px; cursor: pointer; white-space: nowrap;
  }
  .cv-chat .cv-tab.cv-on { color: var(--nd-text-strong); background: var(--nd-w08); }

  .cv-chat .cv-btn {
    border: none; border-radius: 8px; cursor: pointer; font-family: 'DM Sans', sans-serif;
    font-weight: 700; font-size: 12.5px; padding: 9px 16px;
  }
  .cv-chat .cv-btn:disabled { opacity: .5; cursor: not-allowed; }
`;

const STATUS_TABS = [
  { key: 'open', label: 'Open' },
  { key: 'closed', label: 'Closed' },
  { key: 'all', label: 'All' },
];

const INBOX_POLL_MS = 15000;
const THREAD_POLL_MS = 8000;

const imgUrl = (p) => (p ? `${IMG_BASE}${p}` : null);

export default function CommercialChat() {
  const [threads, setThreads] = useState([]);
  const [status, setStatus] = useState('open');
  const [q, setQ] = useState('');

  const [activeId, setActiveId] = useState(null);
  const [thread, setThread] = useState(null);
  const [messages, setMessages] = useState([]);

  const [agents, setAgents] = useState([]);
  const [visits, setVisits] = useState([]);

  const [draft, setDraft] = useState('');
  const [tagId, setTagId] = useState('');
  const [photo, setPhoto] = useState(null);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);

  const [composeOpen, setComposeOpen] = useState(false);
  // schedule_id of the visit whose full detail popup is open, or null.
  const [detailId, setDetailId] = useState(null);

  const bottomRef = useRef(null);
  const fileRef = useRef(null);
  // Held in a ref as well as state so the poller reads the current id without
  // being torn down and rebuilt on every message.
  const activeRef = useRef(null);
  activeRef.current = activeId;

  // ─────────────────────────── loaders ───────────────────────────

  const loadThreads = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/commercial-chat/threads`, {
        ...getAuthHeader(),
        params: { status, q: q.trim() || undefined },
      });
      setThreads(data?.threads || []);
    } catch (err) {
      // The inbox refreshes every 15s; a transient failure must not replace
      // the list the agent is reading with an error card.
      if (!threads.length) setError(err?.response?.data?.error || 'Could not load conversations.');
    }
  }, [status, q, threads.length]);

  const loadThread = useCallback(async (id) => {
    try {
      const [detail, msgs] = await Promise.all([
        axios.get(`${API}/commercial-chat/threads/${id}`, getAuthHeader()),
        axios.get(`${API}/commercial-chat/threads/${id}/messages`, getAuthHeader()),
      ]);
      setThread(detail.data?.thread || null);
      setMessages(msgs.data?.messages || []);
      setError(null);

      // Opening is reading. Per-agent — it does NOT move the team baseline,
      // which only shifts when somebody actually replies.
      await axios.post(`${API}/commercial-chat/threads/${id}/read`, {}, getAuthHeader());
      loadThreads();

      const clientId = detail.data?.thread?.client_id;
      if (clientId) {
        const v = await axios.get(`${API}/commercial-chat/visits/${clientId}`, getAuthHeader());
        setVisits(v.data?.visits || []);
      }
    } catch (err) {
      setError(err?.response?.data?.error || 'Could not open the conversation.');
    }
  }, [loadThreads]);

  const pollThread = useCallback(async () => {
    const id = activeRef.current;
    if (!id) return;
    try {
      const afterId = messages.length ? messages[messages.length - 1].id : undefined;
      const { data } = await axios.get(`${API}/commercial-chat/threads/${id}/messages`, {
        ...getAuthHeader(),
        params: { after_id: afterId },
      });
      const fresh = data?.messages || [];
      if (fresh.length) {
        setMessages((prev) => [...prev, ...fresh]);
        axios.post(`${API}/commercial-chat/threads/${id}/read`, {}, getAuthHeader()).catch(() => {});
      }
    } catch {
      // Silent. The next tick retries.
    }
  }, [messages]);

  // ─────────────────────────── effects ───────────────────────────

  useEffect(() => { loadThreads(); }, [loadThreads]);

  useEffect(() => {
    axios.get(`${API}/commercial-chat/agents`, getAuthHeader())
      .then(({ data }) => setAgents(data?.agents || []))
      .catch(() => setAgents([]));
  }, []);

  useEffect(() => {
    if (activeId) loadThread(activeId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId]);

  // Polling. Both cadences skip while the tab is hidden — an agent with this
  // console parked in a background tab all day should not generate a request
  // every 8 seconds for nothing.
  useEffect(() => {
    const t = setInterval(() => {
      if (!document.hidden) loadThreads();
    }, INBOX_POLL_MS);
    return () => clearInterval(t);
  }, [loadThreads]);

  useEffect(() => {
    const t = setInterval(() => {
      if (!document.hidden) pollThread();
    }, THREAD_POLL_MS);
    return () => clearInterval(t);
  }, [pollThread]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // ─────────────────────────── actions ───────────────────────────

  const send = async () => {
    if (!activeId || sending) return;
    const body = draft.trim();
    if (!body && !photo) return;

    setSending(true);
    setError(null);
    try {
      const form = new FormData();
      if (body) form.append('body', body);
      if (tagId) form.append('schedule_id', tagId);
      if (photo) form.append('photo', photo);

      const { data } = await axios.post(
        `${API}/commercial-chat/threads/${activeId}/messages`,
        form,
        getAuthHeader(),
      );

      setMessages((prev) => [...prev, data.message]);
      setDraft('');
      setTagId('');
      setPhoto(null);
      if (fileRef.current) fileRef.current.value = '';
      loadThreads();
    } catch (err) {
      setError(err?.response?.data?.error || 'Could not send the message.');
    } finally {
      setSending(false);
    }
  };

  const patchThread = async (patch) => {
    if (!activeId) return;
    try {
      const { data } = await axios.patch(
        `${API}/commercial-chat/threads/${activeId}`,
        patch,
        getAuthHeader(),
      );
      setThread((prev) => ({ ...prev, ...data.thread }));
      loadThreads();
    } catch (err) {
      setError(err?.response?.data?.error || 'Could not update the conversation.');
    }
  };

  const totalUnread = useMemo(
    () => threads.reduce((n, t) => n + (t.unread || 0), 0),
    [threads],
  );

  // ─────────────────────────── render ───────────────────────────

  return (
    <div className="cv-chat" style={S.page}>
      <style>{GLOBAL}</style>

      <div style={S.header}>
        <div>
          <div style={S.title}>Commercial Messages</div>
          <div style={S.sub}>
            C&amp;I support · {threads.length} conversation{threads.length === 1 ? '' : 's'}
            {totalUnread > 0 ? ` · ${totalUnread} unread` : ''}
          </div>
        </div>
        <button
          className="cv-btn"
          style={{ background: T.teal, color: '#fff' }}
          onClick={() => setComposeOpen(true)}
        >
          + New conversation
        </button>
      </div>

      {error ? <div style={S.error}>{error}</div> : null}

      <div style={S.body}>
        {/* ── Inbox ─────────────────────────────────────────────── */}
        <div style={S.inbox}>
          <div style={S.tabs}>
            {STATUS_TABS.map((t) => (
              <button
                key={t.key}
                className={`cv-tab ${status === t.key ? 'cv-on' : ''}`}
                onClick={() => setStatus(t.key)}
              >
                {t.label}
              </button>
            ))}
          </div>

          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search client, contact, phone…"
            style={{ width: '100%', marginBottom: 10 }}
          />

          <div style={S.threadList}>
            {threads.length === 0 ? (
              <div style={S.empty}>
                No conversations{status === 'open' ? ' open' : ''} yet.
              </div>
            ) : (
              threads.map((t) => (
                <div
                  key={t.id}
                  className={`cv-thread ${activeId === t.id ? 'cv-on' : ''}`}
                  style={S.threadRow}
                  onClick={() => setActiveId(t.id)}
                >
                  <div style={S.threadTop}>
                    <span style={S.threadName}>{t.client_name}</span>
                    {t.unread > 0 ? <span style={S.badge}>{t.unread}</span> : null}
                  </div>
                  <div style={S.threadPreview}>
                    {t.last_sender_kind === 'agent' ? 'You: ' : ''}
                    {t.last_message_preview || '—'}
                  </div>
                  <div style={S.threadMeta}>
                    {fmtPKTDateTime(t.last_message_at)}
                    {t.assigned_name ? ` · ${t.assigned_name}` : ''}
                    {t.status === 'closed' ? ' · closed' : ''}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* ── Conversation ──────────────────────────────────────── */}
        <div style={S.pane}>
          {!thread ? (
            <div style={S.emptyPane}>Select a conversation, or start a new one.</div>
          ) : (
            <>
              <div style={S.paneHeader}>
                <div>
                  <div style={S.paneTitle}>{thread.client_name}</div>
                  <div style={S.paneSub}>
                    {thread.contact_person || '—'}
                    {thread.contact_number ? ` · ${thread.contact_number}` : ''}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <select
                    value={thread.assigned_user_id || ''}
                    onChange={(e) =>
                      patchThread({ assigned_user_id: e.target.value === '' ? null : Number(e.target.value) })
                    }
                  >
                    <option value="">Unassigned</option>
                    {agents.map((a) => (
                      <option key={a.id} value={a.id}>{a.name || a.email}</option>
                    ))}
                  </select>
                  <button
                    className="cv-btn"
                    style={{ background: 'var(--nd-w08)', color: T.text }}
                    onClick={() => patchThread({ status: thread.status === 'open' ? 'closed' : 'open' })}
                  >
                    {thread.status === 'open' ? 'Close' : 'Reopen'}
                  </button>
                </div>
              </div>

              <div style={S.messages}>
                {messages.map((m) => {
                  const mine = m.sender_kind === 'agent';
                  return (
                    <div key={m.id} style={{ ...S.bubbleRow, justifyContent: mine ? 'flex-end' : 'flex-start' }}>
                      <div style={{ ...S.bubble, ...(mine ? S.bubbleMine : S.bubbleTheirs) }}>
                        {!mine && m.sender_name ? (
                          <div style={S.bubbleSender}>{m.sender_name}</div>
                        ) : null}

                        {m.visit ? (
                          <VisitCard visit={m.visit} mine={mine} onOpen={setDetailId} />
                        ) : null}

                        {m.attachment_url ? (
                          <a href={imgUrl(m.attachment_url)} target="_blank" rel="noreferrer">
                            <img
                              src={imgUrl(m.attachment_url)}
                              alt={m.attachment_name || 'attachment'}
                              style={S.photo}
                            />
                          </a>
                        ) : null}

                        {m.body ? <div style={S.bubbleText}>{m.body}</div> : null}

                        <div style={S.bubbleTime}>
                          {mine && m.sender_name ? `${m.sender_name} · ` : ''}
                          {fmtPKTTime(m.created_at)}
                        </div>
                      </div>
                    </div>
                  );
                })}
                <div ref={bottomRef} />
              </div>

              <div style={S.composer}>
                <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                  <select
                    value={tagId}
                    onChange={(e) => setTagId(e.target.value)}
                    style={{ flex: 1 }}
                  >
                    <option value="">Tag a visit (optional)</option>
                    {visits.map((v) => (
                      <option key={v.id} value={v.id}>
                        {v.scheduled_date} · {v.site_name} · {v.status}
                      </option>
                    ))}
                  </select>
                  <input
                    ref={fileRef}
                    type="file"
                    accept="image/*"
                    onChange={(e) => setPhoto(e.target.files?.[0] || null)}
                    style={{ flex: 1 }}
                  />
                </div>

                <div style={{ display: 'flex', gap: 8 }}>
                  <textarea
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => {
                      // Enter sends, Shift+Enter breaks the line.
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        send();
                      }
                    }}
                    placeholder="Write a reply…"
                    rows={2}
                    style={{ flex: 1, resize: 'vertical' }}
                  />
                  <button
                    className="cv-btn"
                    style={{ background: T.teal, color: '#fff', alignSelf: 'stretch', minWidth: 90 }}
                    onClick={send}
                    disabled={sending || (!draft.trim() && !photo)}
                  >
                    {sending ? 'Sending…' : 'Send'}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {composeOpen ? (
        <ComposeModal
          onClose={() => setComposeOpen(false)}
          onStarted={(threadId) => {
            setComposeOpen(false);
            setStatus('open');
            loadThreads();
            setActiveId(threadId);
          }}
        />
      ) : null}

      {/* The full visit, opened from a tagged message. Keyed on the id so
          switching from one tagged visit to another remounts and refetches
          rather than showing the previous job's photos for a beat. */}
      {detailId ? (
        <JobDetailModal key={detailId} scheduleId={detailId} onClose={() => setDetailId(null)} />
      ) : null}
    </div>
  );
}

/**
 * Start a conversation with a client who has not written in.
 *
 * The residential console has no equivalent — its inbox hides message-less
 * threads to avoid 1,463 empty rows, which also removed any way to say "your
 * crew is running late". 19 commercial clients makes a picker practical.
 *
 * Only clients with a portal login are offered: the backend refuses the rest,
 * because a message nobody has an account to read is worse than no message.
 */
function ComposeModal({ onClose, onStarted }) {
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState('');
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    axios.get(`${API}/commercial-chat/clients`, getAuthHeader())
      .then(({ data }) => setClients(data?.clients || []))
      .catch((e) => setErr(e?.response?.data?.error || 'Could not load clients.'));
  }, []);

  const start = async () => {
    if (!clientId || !body.trim() || busy) return;
    setBusy(true);
    setErr(null);
    try {
      const form = new FormData();
      form.append('client_id', clientId);
      form.append('body', body.trim());
      const { data } = await axios.post(`${API}/commercial-chat/threads`, form, getAuthHeader());
      onStarted(data.thread_id);
    } catch (e) {
      setErr(e?.response?.data?.error || 'Could not start the conversation.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={S.modalWrap} onClick={onClose}>
      <div style={S.modal} onClick={(e) => e.stopPropagation()}>
        <div style={S.modalTitle}>New conversation</div>

        {err ? <div style={S.error}>{err}</div> : null}

        <select
          value={clientId}
          onChange={(e) => setClientId(e.target.value)}
          style={{ width: '100%', marginBottom: 10 }}
        >
          <option value="">Choose a client…</option>
          {clients.map((c) => (
            <option key={c.id} value={c.id}>
              {c.client_name}{c.has_thread ? ' (existing conversation)' : ''}
            </option>
          ))}
        </select>

        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={4}
          placeholder="Your message…"
          style={{ width: '100%', marginBottom: 12, resize: 'vertical' }}
        />

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button className="cv-btn" style={{ background: 'var(--nd-w08)', color: T.text }} onClick={onClose}>
            Cancel
          </button>
          <button
            className="cv-btn"
            style={{ background: T.teal, color: '#fff' }}
            onClick={start}
            disabled={busy || !clientId || !body.trim()}
          >
            {busy ? 'Sending…' : 'Send'}
          </button>
        </div>

        <div style={S.modalNote}>
          Only clients with a portal login are listed — anyone else has no account to read it on.
        </div>
      </div>
    </div>
  );
}

const S = {
  page: { padding: 24, background: T.bg, minHeight: '100vh', fontFamily: "'DM Sans', sans-serif", color: T.text },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 },
  title: { fontSize: 22, fontWeight: 800, color: T.textStrong },
  sub: { fontSize: 12.5, color: T.textMuted, marginTop: 2 },
  error: {
    background: 'rgba(248,113,113,0.12)', border: '1px solid rgba(248,113,113,0.3)',
    color: T.red, borderRadius: 8, padding: '10px 14px', fontSize: 12.5, marginBottom: 12,
  },

  body: { display: 'grid', gridTemplateColumns: 'minmax(280px, 360px) 1fr', gap: 16, alignItems: 'start' },

  inbox: { background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14, padding: 14 },
  tabs: { display: 'flex', gap: 6, marginBottom: 10 },
  threadList: { maxHeight: '65vh', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 2 },
  threadRow: { padding: '10px 12px', borderRadius: 10 },
  threadTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 },
  threadName: { fontSize: 13.5, fontWeight: 700, color: T.textStrong },
  badge: {
    background: T.teal, color: '#fff', borderRadius: 999, fontSize: 11,
    fontWeight: 800, padding: '1px 7px', minWidth: 20, textAlign: 'center',
  },
  threadPreview: { fontSize: 12, color: T.text, marginTop: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  threadMeta: { fontSize: 11, color: T.textMuted, marginTop: 3 },
  empty: { padding: 24, textAlign: 'center', fontSize: 12.5, color: T.textMuted },

  pane: { background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14, display: 'flex', flexDirection: 'column', minHeight: '72vh' },
  emptyPane: { flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: T.textMuted, fontSize: 13 },
  paneHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, padding: 14, borderBottom: `1px solid ${T.border}` },
  paneTitle: { fontSize: 16, fontWeight: 800, color: T.textStrong },
  paneSub: { fontSize: 12, color: T.textMuted, marginTop: 2 },

  messages: { flex: 1, overflowY: 'auto', padding: 16, display: 'flex', flexDirection: 'column', gap: 10, maxHeight: '52vh' },
  bubbleRow: { display: 'flex' },
  bubble: { maxWidth: '72%', borderRadius: 14, padding: '9px 12px' },
  bubbleMine: { background: 'rgba(15,118,110,0.28)', border: '1px solid rgba(45,212,191,0.35)' },
  bubbleTheirs: { background: 'var(--nd-w05)', border: `1px solid ${T.border}` },
  bubbleSender: { fontSize: 11, fontWeight: 800, color: T.tealLight, marginBottom: 3 },
  bubbleText: { fontSize: 13, lineHeight: 1.45, whiteSpace: 'pre-wrap', wordBreak: 'break-word' },
  bubbleTime: { fontSize: 10.5, color: T.textMuted, marginTop: 4, textAlign: 'right' },
  // The visit tag's own styling lives in ChatJobPreview.js — it is a card now,
  // not a chip, and it is shared with the popup that opens from it.
  photo: { maxWidth: 240, borderRadius: 10, display: 'block', marginBottom: 6 },

  composer: { borderTop: `1px solid ${T.border}`, padding: 12 },

  modalWrap: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 60,
  },
  modal: { background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14, padding: 20, width: 'min(520px, 92vw)' },
  modalTitle: { fontSize: 16, fontWeight: 800, color: T.textStrong, marginBottom: 12 },
  modalNote: { fontSize: 11, color: T.textMuted, marginTop: 10 },
};
